import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Agent, GetAgentsResponse } from '@dreamai/types';
import api from '../api';

export interface AgentOption {
  value: string;
  label: string;
  dreamieImageUrl: string | undefined;
  predominantColour: string | undefined;
}

interface AgentContextType {
  selectedAgent: AgentOption | null;
  setSelectedAgent: Dispatch<SetStateAction<AgentOption | null>>;
  agents: Agent[];
  setAgents: Dispatch<SetStateAction<Agent[]>>;
  getAgentById: (agentId: string) => Agent | undefined;
  loading: boolean;
  error: string | null;
  loadAgents: () => Promise<void>;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

interface AgentProviderProps {
  children: ReactNode;
}

export const AgentProvider: React.FC<AgentProviderProps> = ({ children }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [selectedAgent, setSelectedAgent] = useState<AgentOption | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAgentById = (agentId: string): Agent | undefined => {
    return agents.find((agent) => agent.id === agentId);
  };

  const loadAgents = useCallback(async () => {
    // If already loaded and not in error state, don't reload
    if (agents.length > 0 && !error) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const response = await api.get<GetAgentsResponse>('/agents', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.success) {
        setAgents(response.data.agents);
      } else {
        setError('Failed to load agents');
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, agents.length, error]);

  return (
    <AgentContext.Provider
      value={{
        selectedAgent,
        setSelectedAgent,
        agents,
        setAgents,
        getAgentById,
        loading,
        error,
        loadAgents,
      }}
    >
      {children}
    </AgentContext.Provider>
  );
};

export const useAgent = (): AgentContextType => {
  const context = useContext(AgentContext);
  if (context === undefined) {
    throw new Error('useAgent must be used within an AgentProvider');
  }
  return context;
};
